import { DatePicker } from '@/src/components/DatePicker/DatePicker'
import { GranularitySelect } from '@/src/components/GranularitySelect'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useLoadPlanningCurrentLocation } from '@/src/features/procurement-and-markets/load-planning/contexts/LoadPlanningCurrentLocationContext'
import { useLoadedStrategyMetadata } from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import {
  generateDatePickerPreset,
  getDatePickerTypeFromGranularity,
  getDateRangeOfYear,
  getGranularityOptions,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { LoadPlanningGraph } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanningGraph'
import { getCurrentQuarterOfYearRange } from '@/src/utils/dateUtil'
import { Spinner } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mapToEnergyTypeDto } from '../../details/types/adjustment.utils'

export const LoadPlanningConsumptionVisuals = () => {
  const { t } = useTranslation()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId } = useLocationsContext()
  const { locationId } = useLoadPlanningCurrentLocation()

  const [granularity, setGranularity] = useState<GranularityEnum>(GranularityEnum.ONE_MONTH)

  const dateRangeLimits = useMemo(() => getDateRangeOfYear(strategyMetadata?.year), [strategyMetadata?.year])

  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => ({
    from: dateRangeLimits.minDate || new Date(),
    to: dateRangeLimits.maxDate || new Date(),
  }))

  if (!strategyMetadata || !companyPgId) return <Spinner />

  const onGranularityChange = (e: GranularityEnum) => {
    setGranularity(e)
    if (e === GranularityEnum.FIFTEEN_MINUTES) {
      const [from, to] = getCurrentQuarterOfYearRange(Number(strategyMetadata.year))
      setDateRange({ from, to })
    }
    if (e === GranularityEnum.ONE_MONTH && dateRangeLimits.minDate && dateRangeLimits.maxDate) {
      setDateRange({ from: dateRangeLimits.minDate, to: dateRangeLimits.maxDate })
    }
  }

  const onDateChange = ([from, to]: [Date | null, Date | null]) => {
    if (from && to) {
      setDateRange({ from, to })
    }
  }

  return (
    <FlexCol gap={4}>
      <FlexRow gap={4} justifyContent={'flex-end'} width={'100%'}>
        <GranularitySelect
          value={granularity}
          onChange={onGranularityChange}
          options={getGranularityOptions(mapToEnergyTypeDto(strategyMetadata?.energyType), t)}
        />
        <DatePicker
          pickerType={getDatePickerTypeFromGranularity(granularity)}
          isRange={true}
          value={[dateRange.from, dateRange.to]}
          onChange={onDateChange}
          minDate={dateRangeLimits.minDate}
          maxDate={dateRangeLimits.maxDate}
          presets={
            granularity === GranularityEnum.FIFTEEN_MINUTES || granularity === GranularityEnum.ONE_HOUR
              ? generateDatePickerPreset(Number(strategyMetadata.year))
              : undefined
          }
        />
      </FlexRow>

      <FlexCol flex={1}>
        <LoadPlanningGraph
          forecastYear={Number(strategyMetadata.year)}
          energyType={mapToEnergyTypeDto(strategyMetadata?.energyType)}
          companyId={companyPgId}
          granularity={granularity}
          locationId={locationId !== ALL_LOCATIONS ? locationId : undefined}
          dateRange={dateRange}
        />
      </FlexCol>
    </FlexCol>
  )
}
